const UNSTABLE_FORMAT_OPTIONS = [
    'signDisplay',
    'compactDisplay'
];
const UNSTABLE_FORMAT_OPTION_VALUES = {
    currencyDisplay: 'narrowSymbol',
    currencySign: 'accounting',
    style: 'unit'
};
const SHOULD_USE_SAFARI_14_PATCH = (() => {
    /**
     * Here we do a "sniff test" to see if this browser misbehaves in the specific way that
     * would be fixed by our Safari 14.x patch below.
     */
    try {
        (123).toLocaleString('en-US', {
            style: 'unit',
            unit: 'kilometer'
        });
        (123).toLocaleString('en-US', {
            signDisplay: 'exceptZero'
        });
        (123).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            currencyDisplay: 'narrowSymbol',
            currencySign: 'accounting'
        });
        return false;
    }
    catch (error) {
        console.error('DETECTED INCOMPLETE BROWSER SUPPORT FOR Intl.NumberFormat:');
        console.error(error);
        console.warn('ENABLING FALLBACK BEHAVIOR');
        /**
         * We used to report a synthetic error to Sentry here in order to track the
         * prevalence of this fallback behavior and validate that only legacy
         * browsers were being impacted. After a few months we were able to confirm
         * that only legacy browsers belonging to a very small fraction of visitors
         * were hitting this fallback, and so the Sentry error was disabled. See
         * below for the mechanism we were previously using to report this error;
         * it may be useful if we encounter unexpected behavior with our unit
         * formatting implementation in the future.
         *
         * setTimeout(() => {
         *   throw new Error(
         *     'Detected incomplete browser support for Intl.NumberFormat.'
         *   );
         * }, 0);
         */
        return true;
    }
})();
/**
 * Patch an uncommon Safari 14.1 issue with various NumberFormat options. This is based on
 * the investigation and fix that Google shipped when they encountered this same issue:
 *
 * @see https://github.com/google/site-kit-wp/issues/3255
 * @see https://github.com/google/site-kit-wp/pull/3620/files#diff-43329dbb491c976dcedecffe512e85e5e1cc9afa594a82fd72d6fc29ccc0da1bR308-R318
 */
export const toSafeLocaleString = (number, locale, options = {}) => {
    /**
     * This function is a transparent passthru UNLESS we've decided we're in a
     * non-trustworthy browser context:
     */
    if (!SHOULD_USE_SAFARI_14_PATCH) {
        return number.toLocaleString(locale, options);
    }
    /**
     * If we get here then it means we don't trust this browser and we're going try to
     * thread the needle in terms of its support for Intl.NumberFormat options.
     */
    try {
        return number.toLocaleString(locale, options);
    }
    catch (error) {
        console.error(`Caught toLocaleString error: ${JSON.stringify({
            number,
            locale,
            options
        })}`);
        console.error(error instanceof Error ? error.message : error);
        const optionEntries = Object.entries(options ?? {});
        const sanitizedOptions = Object.fromEntries(optionEntries.reduce((memo, [key, value]) => {
            if (!UNSTABLE_FORMAT_OPTIONS.includes(key) &&
                !(key in UNSTABLE_FORMAT_OPTION_VALUES &&
                    UNSTABLE_FORMAT_OPTION_VALUES[key] === value)) {
                memo.push([key, value]);
            }
            return memo;
        }, []));
        return number.toLocaleString(locale, sanitizedOptions);
    }
};
